/**
 * Cornerstone "Sync checked-in patients" setting (EN-1475).
 *
 * The endpoints take no account id: the server resolves the clinic from the session, and `apiFetch`
 * supplies `X-Effective-User-Id` / `X-HappyDoc-Account-Id`. The account id in the query key below
 * is only a cache scope.
 */

import { apiFetch } from "@common/lib/api";

const ENDPOINT = "integration/cornerstone/checked-in-sync";

export const checkedInSyncQueryKey = (accountId?: string) =>
    ["integration", "cornerstone", "checked-in-sync", accountId] as const;

/** `apiFetch` returns `response.json()` whole, so the payload is read through the envelope. */
type ApiEnvelope<T> = { status?: string; data?: T };

function readEnabled(response: ApiEnvelope<{ enabled: boolean }> | undefined): boolean {
    if (!response) throw new Error("Checked-in sync: empty response");
    const enabled = response.data?.enabled;
    // false is a legitimate value, so test the type rather than truthiness.
    if (typeof enabled !== "boolean") throw new Error("Checked-in sync: unexpected response");
    return enabled;
}

export async function fetchCheckedInSync(): Promise<boolean> {
    return readEnabled(await apiFetch<ApiEnvelope<{ enabled: boolean }>>(ENDPOINT, { method: "GET" }));
}

/**
 * Throws on failure (409 when there is no active Cornerstone happy-bridge installation), so callers
 * can revert optimistic UI. Silent: the caller shows its own message, and a 409 is an expected
 * state rather than a bug to report to Sentry.
 */
export async function putCheckedInSync(enabled: boolean): Promise<boolean> {
    return readEnabled(
        await apiFetch<ApiEnvelope<{ enabled: boolean }>>(ENDPOINT, {
            method: "PUT",
            json: { enabled },
            silent: true,
        }),
    );
}
